import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle2, XCircle, Loader2, KeyRound, ExternalLink } from "lucide-react";

// Field groupings per platform. `secret: true` masks the input by default.
const PLATFORM_FIELDS = {
  twitter: {
    label: "X / Twitter",
    fields: [
      { key: "TWITTER_API_KEY", label: "API Key (Consumer Key)", secret: true },
      { key: "TWITTER_API_SECRET", label: "API Secret (Consumer Secret)", secret: true },
      { key: "TWITTER_ACCESS_TOKEN", label: "Access Token", secret: true },
      { key: "TWITTER_ACCESS_TOKEN_SECRET", label: "Access Token Secret", secret: true },
    ],
    docs: "https://developer.twitter.com/en/portal/dashboard",
    helper: "Create a project in the X Developer Portal with Read & Write permissions, then generate consumer keys + access tokens for your account.",
  },
  facebook: {
    label: "Facebook Page",
    fields: [
      { key: "FACEBOOK_PAGE_ID", label: "Page ID", secret: false },
      { key: "FACEBOOK_PAGE_ACCESS_TOKEN", label: "Page Access Token (long-lived)", secret: true },
    ],
    docs: "https://developers.facebook.com/tools/explorer",
    helper: "Use the Graph API Explorer to grab a long-lived Page Access Token with pages_manage_posts + pages_show_list permissions.",
  },
  instagram: {
    label: "Instagram Business",
    fields: [
      { key: "INSTAGRAM_USER_ID", label: "Instagram Business Account ID", secret: false },
      { key: "INSTAGRAM_ACCESS_TOKEN", label: "Access Token (optional — reuses FB Page token if blank)", secret: true },
    ],
    docs: "https://developers.facebook.com/docs/instagram-api/getting-started",
    helper: "Convert @africaartmasters to a Business account and link it to your Facebook Page. The 17-digit ID comes from /me/accounts → instagram_business_account.",
  },
};

function CredField({ field, sourceMeta, value, onChange, onClear, autoReveal }) {
  const [reveal, setReveal] = useState(autoReveal || !field.secret);
  const source = sourceMeta?.source || "missing";
  const preview = sourceMeta?.preview || "";
  const hasSaved = source === "db" || source === "env";
  const canClear = source === "db" && (value === null || value === "" || value === undefined);

  return (
    <div data-testid={`cred-row-${field.key}`} className="border border-[#E5E0D8] bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <label className="label-eyebrow text-[#1E3A8A] block">{field.label}</label>
          <p className="font-mono-aam text-[10px] text-[#888] mt-0.5">{field.key}</p>
        </div>
        <span
          className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 border ${
            source === "db"
              ? "border-[#2E7D32]/40 bg-[#E8F5E9] text-[#1B5E20]"
              : source === "env"
              ? "border-[#1E3A8A]/40 bg-[#E3F2FD] text-[#0D47A1]"
              : "border-[#E5E0D8] bg-[#F4EFE6] text-[#A87900]"
          }`}
        >
          {source === "db" ? "Saved" : source === "env" ? "Env" : "Missing"}
        </span>
      </div>

      {hasSaved && value === null && (
        <p className="font-mono-aam text-xs text-[#555] mt-2" data-testid={`cred-preview-${field.key}`}>
          Current: {preview || "—"}
        </p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <input
          data-testid={`cred-input-${field.key}`}
          type={reveal ? "text" : "password"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hasSaved ? "Leave blank to keep current · type to replace" : "Paste value here"}
          className="flex-1 border border-[#E5E0D8] bg-white px-3 py-2 text-sm font-mono-aam focus:outline-none focus:border-[#1E3A8A]"
          autoComplete="off"
        />
        {field.secret && (
          <button
            type="button"
            onClick={() => setReveal((r) => !r)}
            className="border border-[#E5E0D8] bg-white p-2 hover:border-[#1E3A8A]"
            title={reveal ? "Hide" : "Reveal"}
            data-testid={`cred-reveal-${field.key}`}
          >
            {reveal ? <EyeOff className="w-4 h-4 text-[#555]" /> : <Eye className="w-4 h-4 text-[#555]" />}
          </button>
        )}
      </div>

      {canClear && (
        <button
          type="button"
          onClick={onClear}
          data-testid={`cred-clear-${field.key}`}
          className="mt-2 text-[10px] uppercase tracking-wider text-[#C62828] hover:text-[#111]"
        >
          Clear override (fall back to env)
        </button>
      )}
    </div>
  );
}

export default function SocialCredentialsEditor({ credentials = {}, onSaved }) {
  // `drafts` only holds values the admin has actively typed. Blank = keep current.
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));

  // Explicit single-key clear: fires the PUT immediately so admin doesn't
  // need the type-then-delete-then-save dance. Backend treats "" as "drop override".
  const clearKey = async (key) => {
    try {
      await api.put("/admin/social/credentials", { [key]: "" });
      toast.success(`${key} override cleared`);
      onSaved && (await onSaved());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Clear failed");
    }
  };

  const savePlatform = async (platformId) => {
    const fields = PLATFORM_FIELDS[platformId].fields;
    const payload = {};
    for (const f of fields) {
      if (drafts[f.key] !== undefined) payload[f.key] = drafts[f.key];
    }
    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving((s) => ({ ...s, [platformId]: true }));
    try {
      const { data } = await api.put("/admin/social/credentials", payload);
      const touched = data.updated?.length || 0;
      const cleared = data.cleared?.length || 0;
      toast.success(`Saved · ${touched} updated${cleared ? `, ${cleared} cleared` : ""}`);
      // Wipe the drafts we just persisted so they show as previews next render.
      setDrafts((d) => {
        const next = { ...d };
        for (const f of fields) delete next[f.key];
        return next;
      });
      onSaved && (await onSaved());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [platformId]: false }));
    }
  };

  const testPlatform = async (platformId) => {
    setTesting((t) => ({ ...t, [platformId]: true }));
    try {
      const { data } = await api.post(`/admin/social/test/${platformId}`);
      setTestResults((r) => ({ ...r, [platformId]: data }));
      if (data.ok) {
        toast.success(`Connected · ${data.account}`);
      } else {
        toast.error(data.error || "Connection failed");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Test failed";
      setTestResults((r) => ({ ...r, [platformId]: { ok: false, error: msg } }));
      toast.error(msg);
    } finally {
      setTesting((t) => ({ ...t, [platformId]: false }));
    }
  };

  return (
    <section className="mt-12" data-testid="social-credentials-editor">
      <p className="label-eyebrow text-[#1E3A8A] flex items-center gap-2">
        <KeyRound className="w-3.5 h-3.5" />— Credentials
      </p>
      <h2 className="font-serif-display text-2xl text-[#111] mt-1">Manage platform tokens</h2>
      <p className="text-sm text-[#555] mt-2 max-w-3xl">
        Paste tokens directly from the platform developer portals — they&apos;re stored encrypted-at-rest in your database
        and override the server&apos;s env vars. Leave a field blank to keep the existing value. Empty + save to clear an
        override and fall back to env.
      </p>

      <div className="mt-6 space-y-8">
        {Object.entries(PLATFORM_FIELDS).map(([platformId, cfg]) => {
          const result = testResults[platformId];
          return (
            <div key={platformId} data-testid={`cred-platform-${platformId}`} className="border-l-2 border-[#1E3A8A] pl-5">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <h3 className="font-serif-display text-xl text-[#111]">{cfg.label}</h3>
                <a
                  href={cfg.docs}
                  target="_blank"
                  rel="noreferrer"
                  className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1"
                  data-testid={`cred-docs-${platformId}`}
                >
                  Get tokens <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <p className="text-xs text-[#555] mt-1 max-w-2xl">{cfg.helper}</p>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {cfg.fields.map((field) => (
                  <CredField
                    key={field.key}
                    field={field}
                    sourceMeta={credentials[field.key]}
                    value={drafts[field.key] === undefined ? null : drafts[field.key]}
                    onChange={(v) => setDraft(field.key, v)}
                    onClear={() => clearKey(field.key)}
                  />
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button
                  data-testid={`save-creds-${platformId}`}
                  onClick={() => savePlatform(platformId)}
                  disabled={saving[platformId]}
                  className="bg-[#111] text-white text-xs uppercase tracking-wider px-4 py-2 hover:bg-[#1E3A8A] disabled:opacity-50"
                >
                  {saving[platformId] ? "Saving…" : "Save changes"}
                </button>
                <button
                  data-testid={`test-conn-${platformId}`}
                  onClick={() => testPlatform(platformId)}
                  disabled={testing[platformId]}
                  className="border border-[#1E3A8A] text-[#1E3A8A] text-xs uppercase tracking-wider px-4 py-2 hover:bg-[#1E3A8A] hover:text-white disabled:opacity-50 flex items-center gap-2"
                >
                  {testing[platformId] ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Testing…
                    </>
                  ) : (
                    "Test connection"
                  )}
                </button>
                {result && (
                  <span
                    data-testid={`test-result-${platformId}`}
                    className={`text-xs flex items-center gap-1.5 ${
                      result.ok ? "text-[#1B5E20]" : "text-[#C62828]"
                    }`}
                  >
                    {result.ok ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Live · {result.account}
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3.5 h-3.5" />
                        {result.error?.slice(0, 90) || "Failed"}
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
